import React, { RefObject, useCallback, useEffect, useRef } from 'react';

import { useAppDispatch, useAppTranslation } from 'app/hooks';
import { useChatConntext } from 'components/Hoc/MessageProvider';
import Typo from 'consts/typography';
import { onChangeAuthPage } from 'features/home/homeSlice';
import useReplyMessage from 'hooks/useReplyMesssage';
import useUserDetail from 'hooks/useUserDetail';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from 'redux/userSlice';
import ChatInput from './ChatInput';
import OtherMessage from './OtherMessage';
import { ChatMessageTypeRequest } from 'models/chatMessage';

interface ChatBoxProps {
  className?: string;
}

const ChatBox: React.FunctionComponent<ChatBoxProps> = ({ className = '' }) => {
  const dispatch = useAppDispatch();
  const t = useAppTranslation();
  const user = useSelector(selectCurrentUser);
  const {
    globalMessages,
    sendMessage,
    getMessages,
    canDelete,
    hasMore,
    loading,
    lastMessageId,
    setLoading,
  } = useChatConntext();

  const { isUserLoggedIn } = useUserDetail();

  const { clearMessage } = useReplyMessage();

  const chatRoomRef = useRef<HTMLDivElement>(null);
  const ulRef: RefObject<HTMLUListElement> = useRef(null);

  const handleScroll = () => {
    const chatContainer = chatRoomRef.current;
    if (!chatContainer) return;

    if (chatContainer.scrollTop === 0 && hasMore && !loading) {
      const previousScrollHeight = chatContainer.scrollHeight; // Save scroll height before loading
      setLoading(true);

      setTimeout(() => {
        getMessages(() => {
          requestAnimationFrame(() => {
            if (chatContainer) {
              const newScrollHeight = chatContainer.scrollHeight;
              chatContainer.scrollTop = newScrollHeight - previousScrollHeight;
            }
          });
        });
        setLoading(false);
      }, 1500);
    }
  };

  const onShowLoginPage = () => {
    dispatch(onChangeAuthPage(0));
  };

  useEffect(() => {
    return () => {
      clearMessage();
    };
  }, []);

  const scrollToListItem = (messageId: string) => {
    const index = globalMessages.findIndex((message) => message.messageId === messageId);
    if (ulRef.current) {
      const listItem = ulRef.current.children[index];
      if (listItem instanceof HTMLElement) {
        listItem.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  useEffect(() => {
    if (lastMessageId) scrollToListItem(lastMessageId);
  }, [lastMessageId]);

  const scrollToBottom = () => {
    if (chatRoomRef.current) {
      chatRoomRef.current.scrollTop = chatRoomRef.current.scrollHeight;
      chatRoomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const onSendMessage = (message: ChatMessageTypeRequest) => {
    const isSuccess = sendMessage(message);
    if (isSuccess) {
      setTimeout(() => scrollToBottom(), 200);
    }
  };

  return (
    <div className={`chat-room__chatbox ${className}`}>
      <div className="chat-room__chatbox-messages" ref={chatRoomRef} onScroll={handleScroll}>
        {loading && <p className={`primary-text-color ${Typo.p4} text-center`}>Loading...</p>}

        <ul ref={ulRef}>
          {globalMessages
            ?.filter((chat) => chat.type === 'MESSAGE')
            .map((chat, index) => (
              <OtherMessage
                chat={chat}
                key={chat.messageId + index}
                isAssistantChat={false}
                onScrollMessage={scrollToListItem}
                canDelete={canDelete}
              />
            ))}
        </ul>
      </div>
      {isUserLoggedIn ? (
        user && user.blockChat ? (
          <p>{t('chat.warning')}</p>
        ) : (
          <ChatInput sendMessage={onSendMessage} />
        )
      ) : (
        <button type="button" className="login-btn" onClick={onShowLoginPage}>
          {t('joinChat')}
        </button>
      )}
    </div>
  );
};

export default ChatBox;
